import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, FileText, CalendarCheck, CalendarDays, Users, CalendarRange, BarChart, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [todayAppointments, setTodayAppointments] = useState(0);
  const [appointmentsBookedToday, setAppointmentsBookedToday] = useState(0);
  const [pendingDocuments, setPendingDocuments] = useState(0);
  const [totalConfirmedAppointments, setTotalConfirmedAppointments] = useState(0);
  const [totalClients, setTotalClients] = useState(0);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        
        // Fetch today's appointments
        const { count: appointmentsCount, error: appointmentsError } = await supabase
          .from('appointments')
          .select('*', { count: 'exact', head: true })
          .eq('preferred_date', today);

        if (appointmentsError) throw appointmentsError;
        setTodayAppointments(appointmentsCount || 0);

        // Fetch appointments booked today
        const { count: bookedTodayCount, error: bookedTodayError } = await supabase
          .from('appointments')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', `${today}T00:00:00`)
          .lte('created_at', `${today}T23:59:59`);

        if (bookedTodayError) throw bookedTodayError;
        setAppointmentsBookedToday(bookedTodayCount || 0);

        // Fetch pending documents
        const { count: documentsCount, error: documentsError } = await supabase
          .from('client_documents')
          .select('*', { count: 'exact', head: true })
          .eq('extraction_status', 'pending');

        if (documentsError) throw documentsError;
        setPendingDocuments(documentsCount || 0);

        // Fetch total confirmed appointments
        const { count: totalConfirmedCount, error: totalConfirmedError } = await supabase
          .from('appointments')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'confirmed');

        if (totalConfirmedError) throw totalConfirmedError;
        setTotalConfirmedAppointments(totalConfirmedCount || 0);

        // Fetch total clients
        const { count: clientsCount, error: clientsError } = await supabase
          .from('clients')
          .select('*', { count: 'exact', head: true })
          .eq('client_status', 'active');

        if (clientsError) throw clientsError;
        setTotalClients(clientsCount || 0);
      } catch (error) {
        console.error('Error fetching dashboard counts:', error);
      }
    };

    fetchCounts();

    // Subscribe to real-time changes
    const appointmentsChannel = supabase
      .channel('dashboard-appointments')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments' }, fetchCounts)
      .subscribe();

    const documentsChannel = supabase
      .channel('dashboard-documents')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'client_documents' }, fetchCounts)
      .subscribe();

    const clientsChannel = supabase
      .channel('dashboard-clients')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'clients' }, fetchCounts)
      .subscribe();

    return () => {
      appointmentsChannel.unsubscribe();
      documentsChannel.unsubscribe();
      clientsChannel.unsubscribe();
    };
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-8 p-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight mb-8">Dashboard Overview</h2>
          
          {/* Top 4 Summary Cards */}
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 mb-8 p-2">
            <Card 
              className="admin-stat-card cursor-pointer"
              onClick={() => navigate('/admin/scheduling')}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Today's Appointments
                </CardTitle>
                <Calendar className="h-4 w-4 text-[#5BA05B]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-[#5BA05B]">{todayAppointments}</div>
              </CardContent>
            </Card>

            <Card 
              className="admin-stat-card cursor-pointer"
              onClick={() => navigate('/admin/documents')}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Documents Uploaded
                </CardTitle>
                <FileText className="h-4 w-4 text-[#5BA05B]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-[#5BA05B]">{pendingDocuments}</div>
                <p className="text-xs text-muted-foreground">
                  Pending review
                </p>
              </CardContent>
            </Card>

            <Card 
              className="admin-stat-card cursor-pointer"
              onClick={() => navigate('/admin/scheduling')}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Appointments Booked
                </CardTitle>
                <CalendarCheck className="h-4 w-4 text-[#5BA05B]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-[#5BA05B]">{appointmentsBookedToday}</div>
                <p className="text-xs text-muted-foreground">
                  Bookings made today
                </p>
              </CardContent>
            </Card>

            <Card 
              className="admin-stat-card cursor-pointer"
              onClick={() => navigate('/admin/scheduling')}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Apps Booked
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-[#5BA05B]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-[#5BA05B]">{totalConfirmedAppointments}</div>
                <p className="text-xs text-muted-foreground">
                  All confirmed
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Bottom 4 Feature Cards */}
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 p-2">
            <Card className="admin-card admin-card-accent cursor-pointer" onClick={() => navigate('/admin/clients')}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Users className="h-8 w-8 text-[#5BA05B]" />
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <CardTitle className="text-xl">Client Management</CardTitle>
                <CardDescription className="min-h-[40px]">
                  View and manage all clients
                </CardDescription>
                <div className="pt-2">
                  <p className="text-sm font-medium text-muted-foreground">{totalClients} total clients</p>
                  <p className="text-sm font-medium pt-2 text-[#5BA05B]">View All Clients →</p>
                </div>
              </CardContent>
            </Card>

            <Card className="admin-card admin-card-accent cursor-pointer" onClick={() => navigate('/admin/scheduling')}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Calendar className="h-8 w-8 text-[#5BA05B]" />
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <CardTitle className="text-xl">Appointment Management</CardTitle>
                <CardDescription className="min-h-[40px]">
                  Manage appointments and scheduling
                </CardDescription>
                <div className="pt-2">
                  <p className="text-sm font-medium text-muted-foreground">{totalConfirmedAppointments} upcoming appointments</p>
                  <p className="text-sm font-medium pt-2 text-[#5BA05B]">View Schedule →</p>
                </div>
              </CardContent>
            </Card>

            <Card className="admin-card admin-card-accent cursor-pointer" onClick={() => navigate('/admin/scheduling/calendar')}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CalendarRange className="h-8 w-8 text-[#5BA05B]" />
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <CardTitle className="text-xl">Calendar View</CardTitle>
                <CardDescription className="min-h-[40px]">
                  View appointments in calendar format
                </CardDescription>
                <div className="pt-2">
                  <p className="text-sm font-medium text-muted-foreground">Week and month views</p>
                  <p className="text-sm font-medium pt-2 text-[#5BA05B]">Open Calendar →</p>
                </div>
              </CardContent>
            </Card>

            <Card className="admin-card admin-card-accent cursor-pointer" onClick={() => navigate('/admin/reports')}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <BarChart className="h-8 w-8 text-[#5BA05B]" />
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <CardTitle className="text-xl">Reports & Analytics</CardTitle>
                <CardDescription className="min-h-[40px]">
                  View appointment data and statistics
                </CardDescription>
                <div className="pt-2">
                  <p className="text-sm font-medium text-muted-foreground">Day, week, month, year</p>
                  <p className="text-sm font-medium pt-2 text-[#5BA05B]">View Reports →</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
